import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { FaArrowLeft, FaCheck, FaColumns, FaCopy, FaFileExcel, FaPaste, FaPlus, FaRedo, FaTrash, FaUndo } from "react-icons/fa";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axiosInstance from "../../axiosInstance";
import { fetchAccountingPermissions, ACCOUNTING_SO_CHI_SALARY } from "../../libs/customerCreatePermission";
import { groupMoney, unformatMoney } from "./utils/moneyFormat";
import "./accountingResponsive.css";

const STATUS_MAP = {
  DRAFT: { label: "Nháp", bg: "secondary" },
  APPROVED: { label: "Đã duyệt", bg: "info" },
  REFLECTED: { label: "Đã phản ánh", bg: "success" },
};

const SALARY_TYPES = [
  { value: "OFFICIAL", label: "Chính thức" },
  { value: "SERVICE", label: "CTV / Dịch vụ" },
];

const unwrap = (res) => res?.data?.data ?? res?.data ?? null;

const fmt = (v) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

// Cột nhập chỉnh sửa được.
const INPUT_COLS = [
  { key: "standardWorkDays", label: "Ngày công chuẩn", sm: true },
  { key: "baseInsurance", label: "Lương CB (đóng BH)" },
  { key: "baseSalary", label: "Lương cơ bản" },
  { key: "actualWorkDays", label: "Công thực tế", sm: true },
  { key: "holidayDays", label: "Nghỉ lễ 100%", sm: true },
  { key: "allowance", label: "Phụ cấp" },
  { key: "overtimeHours", label: "Giờ làm thêm", sm: true },
  { key: "revenue", label: "Doanh số" },
  { key: "revenueSalary", label: "Lương theo DS" },
  { key: "bonus", label: "Thưởng DS/TN" },
  { key: "personalDeduction", label: "Giảm trừ bản thân" },
  { key: "dependentDeduction", label: "Giảm trừ gia cảnh" },
  { key: "advance", label: "Tạm ứng" },
];

// Cột tính toán (chỉ đọc).
const CALC_COLS = [
  { key: "overtimePay", label: "Lương làm thêm" },
  { key: "actualSalary", label: "Lương thực tế" },
  { key: "totalIncome", label: "Tổng thu nhập" },
  { key: "insurance", label: "Khấu trừ BH (10.5%)" },
  { key: "unionFee", label: "Công đoàn (1%)" },
  { key: "taxableIncome", label: "TN tính thuế" },
  { key: "personalIncomeTax", label: "Thuế TNCN" },
  { key: "netSalary", label: "Thực lĩnh" },
  { key: "remaining", label: "Còn lại" },
];

const TOGGLE_COLS = [
  { key: "unionFee", label: "Công đoàn (1%)" },
  { key: "baseInsurance", label: "Lương CB (đóng BH)" },
  { key: "allowance", label: "Phụ cấp" },
  { key: "overtimeHours", label: "Giờ làm thêm" },
  { key: "revenue", label: "Doanh số" },
  { key: "revenueSalary", label: "Lương theo DS" },
  { key: "bonus", label: "Thưởng DS/TN" },
  { key: "personalDeduction", label: "Giảm trừ bản thân" },
  { key: "dependentDeduction", label: "Giảm trừ gia cảnh" },
  { key: "advance", label: "Tạm ứng" },
];

const isColVisible = (key, hidden) => {
  if (hidden.has(key)) return false;
  if (key === "overtimePay" && hidden.has("overtimeHours")) return false;
  if ((key === "insurance" || key === "unionFee") && hidden.has("baseInsurance")) return false;
  return true;
};

const TOTAL_KEYS = [
  "baseInsurance", "baseSalary", "actualWorkDays", "holidayDays", "allowance", "overtimeHours",
  "overtimePay", "revenue", "revenueSalary", "bonus", "actualSalary", "totalIncome", "insurance",
  "unionFee", "personalDeduction", "dependentDeduction", "taxableIncome", "personalIncomeTax",
  "netSalary", "advance", "remaining",
];

const SalarySheetDetail = ({ sheetId, onBack, perm: permProp }) => {
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ employeeName: "", position: "", salaryType: "OFFICIAL" });
  const [clipboard, setClipboard] = useState(null);
  const [showColumns, setShowColumns] = useState(false);
  const [columnSelection, setColumnSelection] = useState({});
  const [perm, setPerm] = useState(
    permProp || { canCreate: false, canEdit: false, canDelete: false }
  );

  useEffect(() => {
    if (permProp) {
      setPerm(permProp);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const p = await fetchAccountingPermissions(axiosInstance, ACCOUNTING_SO_CHI_SALARY);
      if (!cancelled) {
        setPerm({
          canCreate: Boolean(p?.canCreate),
          canEdit: Boolean(p?.canEdit),
          canDelete: Boolean(p?.canDelete),
        });
      }
    })();
    return () => { cancelled = true; };
  }, [permProp]);

  const isDraft = sheet?.status === "DRAFT";
  const canEditDraft = isDraft && perm.canEdit;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/accounting/salaries/${sheetId}`);
      const data = unwrap(res);
      setSheet(data);
      const dmap = {};
      (data?.lines || []).forEach((l) => {
        dmap[l.id] = {
          position: l.position ?? "",
          salaryType: l.salaryType ?? "OFFICIAL",
          standardWorkDays: l.standardWorkDays ?? 26,
          baseInsurance: l.baseInsurance ?? 0,
          baseSalary: l.baseSalary ?? 0,
          actualWorkDays: l.actualWorkDays ?? 0,
          holidayDays: l.holidayDays ?? 0,
          allowance: l.allowance ?? 0,
          overtimeHours: l.overtimeHours ?? 0,
          revenue: l.revenue ?? 0,
          revenueSalary: l.revenueSalary ?? 0,
          bonus: l.bonus ?? 0,
          personalDeduction: l.personalDeduction ?? 0,
          dependentDeduction: l.dependentDeduction ?? 0,
          advance: l.advance ?? 0,
        };
      });
      setDrafts(dmap);
    } catch (err) {
      toast.error("Không thể tải bảng lương");
    } finally {
      setLoading(false);
    }
  }, [sheetId]);

  useEffect(() => {
    load();
  }, [load]);

  const lines = useMemo(() => sheet?.lines || [], [sheet]);
  const official = useMemo(() => lines.filter((l) => l.salaryType !== "SERVICE"), [lines]);
  const service = useMemo(() => lines.filter((l) => l.salaryType === "SERVICE"), [lines]);

  const hiddenKeys = useMemo(() => new Set(sheet?.hiddenColumns || []), [sheet]);
  const visibleInputCols = useMemo(
    () => INPUT_COLS.filter((c) => isColVisible(c.key, hiddenKeys)),
    [hiddenKeys]
  );
  const visibleCalcCols = useMemo(
    () => CALC_COLS.filter((c) => isColVisible(c.key, hiddenKeys)),
    [hiddenKeys]
  );

  const totals = useMemo(() => {
    const t = {};
    TOTAL_KEYS.forEach((k) => { t[k] = 0; });
    lines.forEach((l) => {
      TOTAL_KEYS.forEach((k) => { t[k] += Number(l[k] || 0); });
    });
    return t;
  }, [lines]);

  const setField = (lineId, field, value) => {
    setDrafts((prev) => ({ ...prev, [lineId]: { ...prev[lineId], [field]: value } }));
  };

  const copyRow = (lineId) => {
    const d = drafts[lineId] || {};
    const snap = {};
    INPUT_COLS.forEach((c) => { snap[c.key] = d[c.key]; });
    setClipboard(snap);
    toast.success("Đã copy cấu hình lương");
  };

  const pasteRow = (lineId) => {
    if (!clipboard) {
      toast.warn("Chưa có dữ liệu để dán");
      return;
    }
    setDrafts((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...clipboard } }));
    toast.success("Đã dán cấu hình lương");
  };

  const buildLinePayload = () => lines.map((l) => {
    const d = drafts[l.id] || {};
    const row = { id: l.id, position: d.position, salaryType: d.salaryType };
    INPUT_COLS.forEach((c) => {
      row[c.key] = d[c.key] === "" || d[c.key] == null ? 0 : Number(d[c.key]);
    });
    row.standardWorkDays = Number(d.standardWorkDays) > 0 ? Number(d.standardWorkDays) : 26;
    return row;
  });

  const regenerate = async () => {
    setBusy(true);
    try {
      const payload = buildLinePayload();
      await axiosInstance.put(`/api/accounting/salaries/${sheetId}/lines`, payload);
      toast.success("Đã tạo lại bảng lương");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Tạo lại bảng lương thất bại");
    } finally {
      setBusy(false);
    }
  };

  const openColumns = () => {
    const hidden = new Set(sheet?.hiddenColumns || []);
    const next = {};
    TOGGLE_COLS.forEach((c) => { next[c.key] = !hidden.has(c.key); });
    setColumnSelection(next);
    setShowColumns(true);
  };

  const applyColumns = async () => {
    setBusy(true);
    try {
      const hiddenColumns = TOGGLE_COLS.filter((c) => !columnSelection[c.key]).map((c) => c.key);
      const res = await axiosInstance.put(`/api/accounting/salaries/${sheetId}/columns`, {
        hiddenColumns,
        lines: buildLinePayload(),
      });
      const respData = res?.data;
      if (respData && respData.success === false) {
        toast.error(respData.message || "Cập nhật cột thất bại");
        return;
      }
      toast.success("Đã cập nhật cột bảng lương");
      setShowColumns(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Cập nhật cột thất bại");
    } finally {
      setBusy(false);
    }
  };

  const doAction = async (url, successMsg, confirmTitle) => {
    if (confirmTitle) {
      const r = await Swal.fire({
        title: confirmTitle,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Đồng ý",
        cancelButtonText: "Huỷ",
      });
      if (!r.isConfirmed) return;
    }
    setBusy(true);
    try {
      const res = await axiosInstance.put(url);
      const respData = res?.data;
      if (respData && respData.success === false) {
        toast.error(respData.message || "Thao tác thất bại");
      } else {
        toast.success(successMsg);
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Thao tác thất bại");
    } finally {
      setBusy(false);
    }
  };

  const deleteLine = async (lineId) => {
    const r = await Swal.fire({
      title: "Xoá dòng lương này?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Xoá",
      cancelButtonText: "Huỷ",
    });
    if (!r.isConfirmed) return;
    try {
      await axiosInstance.delete(`/api/accounting/salaries/lines/${lineId}`);
      toast.success("Đã xoá dòng");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Xoá thất bại");
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.employeeName.trim()) {
      toast.warn("Vui lòng nhập họ tên");
      return;
    }
    try {
      await axiosInstance.post(`/api/accounting/salaries/${sheetId}/lines`, {
        employeeName: addForm.employeeName.trim(),
        position: addForm.position.trim(),
        salaryType: addForm.salaryType,
        standardWorkDays: 26,
      });
      toast.success("Đã thêm dòng");
      setShowAdd(false);
      setAddForm({ employeeName: "", position: "", salaryType: "OFFICIAL" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Thêm dòng thất bại");
    }
  };

  const exportExcel = async () => {
    try {
      const res = await axiosInstance.get(`/api/accounting/salaries/${sheetId}/export`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bang-luong-${sheet?.period || sheetId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Xuất Excel thất bại");
    }
  };

  const totalColSpan = 5; // TT + Họ tên + Chức vụ + Line KD + Loại lương

  const renderLineTags = (l) => {
    const names = Array.isArray(l.lineNames) ? l.lineNames.filter(Boolean) : [];
    if (!names.length) return <span className="text-muted">—</span>;
    return (
      <div className="d-flex flex-wrap gap-1">
        {names.map((name) => (
          <span key={name} className="select3-item acc-line-tag">{name}</span>
        ))}
      </div>
    );
  };

  const renderRow = (l, idx) => {
    const d = drafts[l.id] || {};
    return (
      <tr key={l.id}>
        <td className="text-center">{idx}</td>
        <td className="acc-sticky-col fw-semibold">{l.employeeName}</td>
        <td>
          {canEditDraft ? (
            <Form.Control size="sm" className="acc-salary-input" value={d.position || ""}
              onChange={(e) => setField(l.id, "position", e.target.value)} />
          ) : (l.position || d.position || "")}
        </td>
        <td style={{ minWidth: 160 }}>{renderLineTags(l)}</td>
        <td>
          {canEditDraft ? (
            <Form.Select size="sm" className="acc-salary-input" value={d.salaryType || "OFFICIAL"}
              onChange={(e) => setField(l.id, "salaryType", e.target.value)}>
              {SALARY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Form.Select>
          ) : (SALARY_TYPES.find((t) => t.value === (l.salaryType || d.salaryType))?.label || l.salaryType)}
        </td>
        {visibleInputCols.map((c) => (
          <td key={c.key} className="text-end">
            {canEditDraft ? (
              c.sm ? (
                <Form.Control type="number" size="sm"
                  className="acc-salary-input acc-salary-input-sm"
                  value={d[c.key] ?? ""}
                  onChange={(e) => setField(l.id, c.key, e.target.value)} />
              ) : (
                <Form.Control type="text" inputMode="numeric" size="sm"
                  className="acc-salary-input"
                  value={groupMoney(d[c.key])}
                  onChange={(e) => setField(l.id, c.key, unformatMoney(e.target.value))} />
              )
            ) : (fmt(l[c.key] ?? d[c.key]))}
          </td>
        ))}
        {visibleCalcCols.map((c) => (
          <td key={c.key} className="text-end text-nowrap">{fmt(l[c.key])}</td>
        ))}
        <td className="text-center text-nowrap" style={{ minWidth: 110 }}>
          {isDraft && (perm.canEdit || perm.canDelete) && (
            <div className="acc-action-btns flex-nowrap">
              {perm.canEdit && (
                <>
                  <Button variant="outline-secondary" size="sm" title="Copy cấu hình" onClick={() => copyRow(l.id)}>
                    <FaCopy />
                  </Button>
                  <Button variant="outline-info" size="sm" title="Dán cấu hình" disabled={!clipboard} onClick={() => pasteRow(l.id)}>
                    <FaPaste />
                  </Button>
                </>
              )}
              {perm.canDelete && (
                <Button variant="outline-danger" size="sm" title="Xoá dòng" onClick={() => deleteLine(l.id)}>
                  <FaTrash />
                </Button>
              )}
            </div>
          )}
        </td>
      </tr>
    );
  };

  const sectionHeader = (title, span) => (
    <tr className="acc-salary-section">
      <td colSpan={span}>{title}</td>
    </tr>
  );

  const totalCols = visibleInputCols.length + visibleCalcCols.length + totalColSpan + 1; // +1 action col
  let officialIdx = 0;
  let serviceIdx = 0;

  const statusInfo = STATUS_MAP[sheet?.status] || { label: sheet?.status, bg: "secondary" };

  return (
    <div>
      <Card className="mb-3">
        <Card.Body className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Button variant="outline-secondary" size="sm" onClick={onBack}><FaArrowLeft className="me-1" /> Danh sách</Button>
            <h6 className="mb-0 fw-bold">{sheet?.title || "Bảng lương"}</h6>
            <Badge bg={statusInfo.bg}>{statusInfo.label}</Badge>
            {sheet?.paymentVoucherId && (
              <span className="small text-muted">Phiếu chi #{sheet.paymentVoucherId}</span>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {isDraft && (
              <>
                {perm.canEdit && (
                  <Button variant="outline-secondary" size="sm" onClick={openColumns} disabled={busy}>
                    <FaColumns className="me-1" /> Cột
                  </Button>
                )}
                {perm.canEdit && (
                  <Button variant="primary" size="sm" onClick={regenerate} disabled={busy}>
                    {busy ? <Spinner animation="border" size="sm" /> : <><FaRedo className="me-1" /> Tạo lại bảng lương</>}
                  </Button>
                )}
                {perm.canCreate && (
                  <Button variant="outline-primary" size="sm" onClick={() => setShowAdd(true)}>
                    <FaPlus className="me-1" /> Thêm dòng
                  </Button>
                )}
                {perm.canEdit && (
                  <Button variant="success" size="sm" disabled={busy}
                    onClick={() => doAction(`/api/accounting/salaries/${sheetId}/approve`, "Đã duyệt", "Duyệt bảng lương? Sau khi duyệt sẽ không chỉnh sửa được.")}>
                    <FaCheck className="me-1" /> Duyệt
                  </Button>
                )}
              </>
            )}
            {perm.canEdit && sheet?.status === "APPROVED" && (
              <>
                <Button variant="warning" size="sm" disabled={busy}
                  onClick={() => doAction(`/api/accounting/salaries/${sheetId}/reflect`, "Đã xác nhận và phản ánh vào dòng tiền", "Xác nhận bảng lương? Hệ thống sẽ tạo phiếu chi và phản ánh vào dòng tiền.")}>
                  <FaCheck className="me-1" /> Xác nhận (tạo phiếu chi)
                </Button>
                <Button variant="outline-secondary" size="sm" disabled={busy}
                  onClick={() => doAction(`/api/accounting/salaries/${sheetId}/reopen`, "Đã mở lại", "Mở lại để chỉnh sửa?")}>
                  <FaUndo className="me-1" /> Mở lại
                </Button>
              </>
            )}
            <Button variant="outline-success" size="sm" onClick={exportExcel}>
              <FaFileExcel className="me-1" /> Xuất Excel
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : lines.length === 0 ? (
            <div className="text-center py-5 text-muted">Bảng lương chưa có nhân viên</div>
          ) : (
            <div className="acc-table-wrap acc-salary-scroll">
              <table className="table table-bordered table-hover align-middle mb-0 acc-salary-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 45 }}>TT</th>
                    <th className="acc-sticky-col" style={{ minWidth: 150 }}>Họ tên</th>
                    <th style={{ minWidth: 130 }}>Chức vụ</th>
                    <th style={{ minWidth: 160 }}>Line KD</th>
                    <th style={{ minWidth: 120 }}>Loại lương</th>
                    {visibleInputCols.map((c) => <th key={c.key} className="text-end">{c.label}</th>)}
                    {visibleCalcCols.map((c) => <th key={c.key} className="text-end">{c.label}</th>)}
                    <th style={{ minWidth: 110 }} className="text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {official.length > 0 && sectionHeader("Bảng lương chính thức", totalCols)}
                  {official.map((l) => renderRow(l, ++officialIdx))}
                  {service.length > 0 && sectionHeader("Bảng lương cộng tác viên / Dịch vụ", totalCols)}
                  {service.map((l) => renderRow(l, ++serviceIdx))}
                  <tr className="acc-salary-total">
                    <td colSpan={totalColSpan} className="text-center">Tổng cộng</td>
                    {visibleInputCols.map((c) => (
                      <td key={c.key} className="text-end">{TOTAL_KEYS.includes(c.key) ? fmt(totals[c.key]) : ""}</td>
                    ))}
                    {visibleCalcCols.map((c) => (
                      <td key={c.key} className="text-end">{TOTAL_KEYS.includes(c.key) ? fmt(totals[c.key]) : ""}</td>
                    ))}
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showColumns} onHide={() => setShowColumns(false)} centered>
        <Modal.Header closeButton><Modal.Title>Cột bảng lương</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="small text-muted">Bỏ chọn một khoản thì cột đó biến mất và không được tính vào lương. Bật lại sẽ dùng số đã nhập. Doanh số chỉ ẩn cột; khoản làm đổi lương là Lương theo DS.</p>
          {TOGGLE_COLS.map((c) => (
            <Form.Check
              key={c.key}
              id={`salary-col-${c.key}`}
              className="mb-2"
              label={c.label}
              checked={Boolean(columnSelection[c.key])}
              onChange={(e) => setColumnSelection((prev) => ({ ...prev, [c.key]: e.target.checked }))}
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowColumns(false)} disabled={busy}>Huỷ</Button>
          <Button variant="primary" onClick={applyColumns} disabled={busy}>
            {busy ? <Spinner animation="border" size="sm" /> : "Áp dụng"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton><Modal.Title>Thêm dòng nhân viên</Modal.Title></Modal.Header>
        <Form onSubmit={submitAdd}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Họ tên</Form.Label>
              <Form.Control value={addForm.employeeName} autoFocus
                onChange={(e) => setAddForm((p) => ({ ...p, employeeName: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Chức vụ</Form.Label>
              <Form.Control value={addForm.position}
                onChange={(e) => setAddForm((p) => ({ ...p, position: e.target.value }))} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Loại lương</Form.Label>
              <Form.Select value={addForm.salaryType}
                onChange={(e) => setAddForm((p) => ({ ...p, salaryType: e.target.value }))}>
                {SALARY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAdd(false)}>Huỷ</Button>
            <Button variant="primary" type="submit">Thêm</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SalarySheetDetail;
